import { Recapt } from '../engine';
import { IdentityUpdaterModule } from '../modules/identityUpdater';
import { UserCommentsModule } from '../modules/userComments';
import { SessionIdentity } from '../types';
import { SESSION_IDENTITY_STORAGE_KEY } from './storageConstants';
import { StorageManager } from './storageManager';

interface User {
  /** Unique identifier for the user */
  uid?: string;
  /** User's email address */
  email?: string;
  /** User's nickname or username */
  nickname?: string;
  /** User's full name */
  fullName?: string;
}

/**
 * Interface representing a company in the system
 */
interface Company {
  /** Unique identifier for the company */
  cid?: string;
  /** Company name */
  name?: string;
}

/**
 * Manages user and company session data.
 * Handles storage, retrieval, and updates of session information.
 */
export class Session {
  private currentIdentity: SessionIdentity = {};

  get hasIdentity() {
    return Object.keys(this.currentIdentity).length > 0;
  }

  /**
   * Initializes the session by loading stored user and company data.
   */
  init() {
    this.currentIdentity =
      StorageManager.getItem(SESSION_IDENTITY_STORAGE_KEY) ?? {};

    if (this.hasIdentity) {
      this.onIdentityChange();
    }
  }

  reset() {
    this.currentIdentity = {};
  }

  /**
   * Updates the current user information.
   * If any existing values differ, the user data is completely replaced and storage is purged.
   * @param user - The user data to set
   */
  public setIdentity(identity: SessionIdentity) {
    let isNewIdentity = false;

    for (const [key, value] of Object.entries(identity)) {
      if (key in this.currentIdentity) {
        const currentValue = this.currentIdentity[key as keyof SessionIdentity];

        if (currentValue === value) continue;

        StorageManager.removeItem(SESSION_IDENTITY_STORAGE_KEY);

        Recapt.debug('detected new identity', this.currentIdentity, identity);

        isNewIdentity = true;
        break;
      }
    }

    if (!isNewIdentity) {
      this.currentIdentity = {
        ...this.currentIdentity,
        ...identity,
      };
    } else {
      this.currentIdentity = identity;
    }

    StorageManager.setItem(SESSION_IDENTITY_STORAGE_KEY, this.currentIdentity);

    this.onIdentityChange();
  }
  setUser(user: User) {
    this.setIdentity({
      ...this.currentIdentity,
      ...user,
    });
  }

  setCompany(company: Company) {
    this.setIdentity({
      ...this.currentIdentity,
      companyName: company.name,
      companyId: company.cid,
    });
  }

  public onIdentityChange() {
    const identityUpdaterModule =
      Recapt.getModule<IdentityUpdaterModule>('identityUpdater');
    if (identityUpdaterModule) {
      identityUpdaterModule.onIdentityChange();
    }

    if (this.hasIdentity) {
      const userCommentModule =
        Recapt.getModule<UserCommentsModule>('userComments');
      if (userCommentModule) {
        userCommentModule.onIdentity();
      }
    }
  }

  public destroy() {
    this.currentIdentity = {};
    StorageManager.removeItem(SESSION_IDENTITY_STORAGE_KEY);
    this.onIdentityChange();
  }

  public getIdentity() {
    return this.currentIdentity;
  }

  /**
   * Checks if the session is ready for use.
   * @returns True if user data is present, false otherwise
   */
  ready() {
    // if (Object.keys(this.currentIdentity).length === 0) return false;
    return true;
  }
}
